export type IdentityProviderDesiredState = 'draft' | 'active' | 'disabled' | 'deleting';
export type IdentityProviderObservedState = 'unvalidated' | 'validating' | 'ready' | 'error' | 'deleted';

export interface TenantIdentityProvider {
  id: string;
  tenantId: string;
  providerType: 'oidc';
  displayName: string;
  loginHostnameLabel: string;
  issuer: string;
  clientId: string;
  desiredState: IdentityProviderDesiredState;
  observedState: IdentityProviderObservedState;
  isDefaultInteractive: boolean;
  loginFallback: 'platform_public';
  emailClaim: string;
  displayNameClaim?: string | null;
  roleClaimPath?: string | null;
  additionalScopes: string[];
  secretPresent: boolean;
  secretRevision?: string | null;
  secretFingerprint?: string | null;
  diagnosticCode?: string | null;
  validatedAt?: string | null;
  reconciledAt?: string | null;
  lastSuccessfulLoginAt?: string | null;
  version: number;
  createdAt: string;
  updatedAt: string;
}

export interface SaveTenantIdentityProviderInput {
  displayName: string;
  issuer: string;
  clientId: string;
  emailClaim: string;
  displayNameClaim?: string | null;
  roleClaimPath?: string | null;
  additionalScopes: string[];
  version?: number;
}

export interface CreateTenantIdentityProviderInput extends Omit<SaveTenantIdentityProviderInput, 'version'> {
  clientSecret: string;
}

export interface TenantOIDCRoleMapping {
  id: string;
  externalRole: string;
  tenantRoleId: string;
  roleCode: string;
  roleName: string;
  permissions: string[];
}

export interface TenantOIDCAvailableRole {
  id: string;
  code: string;
  name: string;
  permissions: string[];
}

export interface TenantOIDCRoleMappingCollection {
  version: number;
  mappings: TenantOIDCRoleMapping[];
  availableRoles: TenantOIDCAvailableRole[];
}

export class TenantIdentityProviderApiError extends Error {
  constructor(public readonly status: number, public readonly code: string) {
    super(messageFor(code, status));
    this.name = 'TenantIdentityProviderApiError';
  }
}

const messageFor = (code: string, status: number): string => ({
  version_conflict: 'This identity provider changed. Refresh and try again.',
  recent_authentication_required: 'Reauthenticate before changing this identity provider.',
  provider_not_ready: 'Validate the identity provider successfully before activation.',
  secret_required: 'Configure the client secret before validation.',
  invalid_issuer: 'Enter an exact supported HTTPS issuer URL.',
  forbidden: 'You do not have permission to manage tenant identity providers.',
}[code] ?? `Identity provider request failed (${status}).`);

const requestId = (prefix: string) => `${prefix}_${crypto.randomUUID()}`;

const csrfToken = (): string => {
  if (typeof document === 'undefined') return '';
  const entry = document.cookie.split(';').map((part) => part.trim()).find((part) => part.startsWith('csrf='));
  if (!entry) return '';
  try {
    return decodeURIComponent(entry.slice('csrf='.length));
  } catch {
    return '';
  }
};

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const method = (init?.method ?? 'GET').toUpperCase();
  const csrf = method === 'GET' || method === 'HEAD' ? '' : csrfToken();
  const response = await fetch(path, {
    credentials: 'include',
    ...init,
    headers: {
      'Content-Type': 'application/json',
      'X-Request-ID': requestId('tenant_oidc'),
      ...(csrf ? { 'X-CSRF-Token': csrf } : {}),
      ...(init?.headers ?? {}),
    },
  });
  if (!response.ok) {
    let code = 'request_failed';
    try {
      const body = await response.json() as { code?: string; error?: string };
      code = body.code ?? body.error ?? code;
    } catch {
      // Keep the safe generic error code; never echo an upstream response body.
    }
    throw new TenantIdentityProviderApiError(response.status, code);
  }
  return response.status === 204 ? undefined as T : response.json() as Promise<T>;
}

const providerPath = (tenantId: string, providerId?: string) =>
  `/api/v1/tenants/${encodeURIComponent(tenantId)}/identity-providers${providerId ? `/${encodeURIComponent(providerId)}` : ''}`;

export const tenantIdentityProvidersApi = {
  list: async (tenantId: string): Promise<TenantIdentityProvider[]> => {
    const payload = await request<TenantIdentityProvider[] | { items: TenantIdentityProvider[] }>(providerPath(tenantId));
    const items = Array.isArray(payload) ? payload : payload.items ?? [];
    return items.map((provider) => ({
      ...provider,
      additionalScopes: Array.isArray(provider.additionalScopes) ? provider.additionalScopes : [],
    }));
  },
  create: (tenantId: string, input: CreateTenantIdentityProviderInput) =>
    request<TenantIdentityProvider>(providerPath(tenantId), {
      method: 'POST',
      headers: { 'Idempotency-Key': requestId('create') },
      body: JSON.stringify(input),
    }),
  update: (tenantId: string, providerId: string, input: SaveTenantIdentityProviderInput) =>
    request<TenantIdentityProvider>(providerPath(tenantId, providerId), {
      method: 'PATCH',
      body: JSON.stringify(input),
    }),
  replaceSecret: (tenantId: string, providerId: string, clientSecret: string, version: number) =>
    request<TenantIdentityProvider>(`${providerPath(tenantId, providerId)}/secret`, {
      method: 'PUT',
      headers: { 'Idempotency-Key': requestId('secret') },
      body: JSON.stringify({ clientSecret, version }),
    }),
  listRoleMappings: (tenantId: string, providerId: string) =>
    request<TenantOIDCRoleMappingCollection>(`${providerPath(tenantId, providerId)}/role-mappings`),
  replaceRoleMappings: (tenantId: string, providerId: string, version: number, mappings: Array<{ externalRole: string; tenantRoleId: string }>) =>
    request<TenantOIDCRoleMappingCollection>(`${providerPath(tenantId, providerId)}/role-mappings`, {
      method: 'PUT',
      body: JSON.stringify({ version, mappings }),
    }),
  action: (tenantId: string, providerId: string, action: 'validate' | 'activate' | 'disable', version: number) =>
    request<TenantIdentityProvider>(`${providerPath(tenantId, providerId)}/${action}`, {
      method: 'POST',
      headers: { 'Idempotency-Key': requestId(action) },
      body: JSON.stringify({ version }),
    }),
  delete: (tenantId: string, providerId: string, version: number) =>
    request<void>(providerPath(tenantId, providerId), {
      method: 'DELETE',
      headers: { 'Idempotency-Key': requestId('delete') },
      body: JSON.stringify({ version }),
    }),
};

export type TenantIdentityProvidersApi = typeof tenantIdentityProvidersApi;
